import type * as React from "react";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "./input-group";
import { useNumericInput } from "./use-numeric-input";

interface IPercentageInputProps
  extends Omit<
    React.ComponentPropsWithoutRef<"input">,
    "onChange" | "value" | "onBlur"
  > {
  onChange?: (value: number | null) => void;
  value?: number | null;
  onBlur?: (value: number | null) => void;
  // Rendered after the % suffix behind a divider — e.g. a SegmentedToggle
  // choosing what the percentage is of, like PercentOrMoneyInput's mode slot.
  trailing?: React.ReactNode;
}

const PercentageInput = ({
  onChange,
  value,
  onBlur,
  onFocus,
  trailing,
  ref,
  ...props
}: IPercentageInputProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const { displayValue, handleFocus, handleChange, handleBlur } =
    useNumericInput({ value, onChange, onBlur, onFocus, type: "float" });

  return (
    <InputGroup className={trailing ? "overflow-hidden" : undefined}>
      <InputGroupInput
        ref={ref}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode="decimal"
        {...props}
      />
      <InputGroupAddon align="inline-end">
        <InputGroupText>%</InputGroupText>
      </InputGroupAddon>
      {trailing && (
        // order-last as well: the % addon is order-last, and DOM order then
        // keeps this slot after it.
        <div className="order-last flex items-center border-l border-gray-300 px-1.5">
          {trailing}
        </div>
      )}
    </InputGroup>
  );
};

PercentageInput.displayName = "PercentageInput";

export type { IPercentageInputProps };
export { PercentageInput };
